import React, { useState, useEffect } from 'react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import ContactSelector from '../../components/contacts/ContactSelector';
import { Mail, User } from 'lucide-react';
import { ISenderAccount } from 'types/senderAccounts/senderAccount.types';
import { IContact } from 'types/contacts/contacts.types';

interface SendTestEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  verifiedSenderAccounts: ISenderAccount[];
  senderAccountsLoading: boolean;
  userEmail: string;
  initialFromAccountId?: string;
  htmlContent?: string;
  sendingTestEmail?: boolean;
  onSend: (
    fromAccountId: string,
    contactId: string,
    emailAddress: string[]
  ) => void;
}

const R = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const parse = (s: string) =>
  s
    .split(/[,;\s]+/)
    .map((e) => e.trim())
    .filter(Boolean);
const valid = (e: string) => e.length > 0 && R.test(e);

export default function SendTestEmailModal({
  isOpen,
  onClose,
  verifiedSenderAccounts,
  senderAccountsLoading,
  userEmail,
  initialFromAccountId,
  htmlContent,
  sendingTestEmail = false,
  onSend,
}: SendTestEmailModalProps) {
  const [fromId, setFromId] = useState('');
  const [contactId, setContactId] = useState('');
  const [emailVal, setEmailVal] = useState(userEmail ?? '');

  useEffect(() => {
    if (isOpen) {
      setFromId(initialFromAccountId ?? '');
      setEmailVal(userEmail ?? '');
    }
  }, [isOpen, initialFromAccountId, userEmail]);

  const emails = parse(emailVal);
  const bad = emails.filter((e) => !valid(e));
  const canSend =
    fromId &&
    contactId &&
    emails.length > 0 &&
    bad.length === 0 &&
    !senderAccountsLoading &&
    !sendingTestEmail;

  const close = () => {
    setFromId('');
    setContactId('');
    setEmailVal('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title="Send Test Email" size="large">
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-foreground">
            From <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <select
              value={fromId}
              onChange={(e) => setFromId(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors hover:border-input appearance-none bg-background disabled:bg-muted disabled:cursor-not-allowed"
              disabled={senderAccountsLoading}
            >
              <option value="">Select sender account</option>
              {verifiedSenderAccounts?.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.name} ({a.email})
                </option>
              ))}
            </select>
          </div>
          {senderAccountsLoading && (
            <p className="text-muted-foreground text-xs mt-0.5">Loading…</p>
          )}
          {!senderAccountsLoading && !verifiedSenderAccounts.length && (
            <p className="text-warning-foreground text-xs mt-0.5">
              No verified accounts
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-foreground">
            To
          </label>
          <div className="relative">
            <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={emailVal}
              onChange={(e) => setEmailVal(e.target.value)}
              className={
                'w-full pl-9 pr-3 py-1.5 text-sm border rounded-lg bg-muted/50 ' +
                (bad.length && emails.length
                  ? 'border-destructive focus:ring-destructive'
                  : 'border-border')
              }
              placeholder="email@example.com, other@example.com"
            />
          </div>
          {bad.length > 0 && emails.length > 0 && (
            <p className="text-destructive text-xs mt-0.5">
              Invalid email format: {bad.join(', ')}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <ContactSelector
            value={contactId}
            onChange={(c: IContact) => setContactId(c._id ?? '')}
            label="Select contact"
            required
            fetchWhenVisible
            isVisible={isOpen}
            autoSelectFirst
          />
        </div>

        {htmlContent && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Preview
            </label>
            <div className="border border-border rounded-lg overflow-hidden bg-card">
              <div className="max-h-100 overflow-y-auto">
                <iframe
                  srcDoc={htmlContent}
                  className="w-full border-0"
                  title="Email Preview"
                  style={{ minHeight: '400px', width: '100%' }}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => canSend && onSend(fromId, contactId, emails)}
            disabled={!canSend}
          >
            {sendingTestEmail ? (
              <span className="inline-flex items-center gap-2">
                <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-primary-foreground border-t-transparent" />
                Sending…
              </span>
            ) : (
              'Send test email'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
